import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { notificationService } from '../services';
import { getSocket, onNotification } from '../services/socket';

const NotificationContext = createContext(null);

function mergeUnique(existing, incoming) {
  const seen = new Set(existing.map((r) => String(r._id)));
  const out = [...existing];
  for (const row of incoming) {
    const id = String(row._id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(row);
  }
  return out;
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const fetchedRef = useRef(false);

  /**
   * Counted by the server, not by filtering the loaded page.
   *
   * `items` holds one page (30 by default), so deriving the badge from it capped
   * the number at 30 and made it grow as the user scrolled — a badge that looks
   * exact and is not. `unreadCount` falls back to the local tally only before the
   * first count lands, so the badge is never blank on a cold render.
   */
  const [serverUnread, setServerUnread] = useState(null);
  const localUnread = useMemo(() => items.filter((n) => !n.readAt).length, [items]);
  const unreadCount = serverUnread == null ? localUnread : serverUnread;

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const res = await notificationService.mineUnreadCount();
      const data = res.data?.data || res.data || {};
      if (typeof data.count === 'number') setServerUnread(data.count);
    } catch {
      // A failed count must never blank the bell — keep the local tally.
    }
  }, [user]);

  const fetchMine = useCallback(async ({ cursor, append = false } = {}) => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await notificationService.mine(cursor ? { cursor } : undefined);
      const data = res.data?.data || res.data || {};
      const rows = data.items || [];
      setItems((prev) => (append ? mergeUnique(prev, rows) : rows));
      setNextCursor(data.nextCursor || null);
      fetchedRef.current = true;
    } finally {
      setLoading(false);
    }
  }, [user]);

  const refresh = useCallback(async () => {
    await Promise.all([fetchMine(), refreshUnread()]);
  }, [fetchMine, refreshUnread]);

  useEffect(() => {
    if (!user) {
      setItems([]);
      setNextCursor(null);
      setServerUnread(null);
      fetchedRef.current = false;
      return undefined;
    }

    refresh();

    const socket = getSocket();
    const onConnect = () => refresh();
    socket?.on('connect', onConnect);

    const offNotify = onNotification(() => {
      // Refetch after the outbox cron delivers — avoids duplicate synthetic rows (2-T4).
      refresh();
    });

    return () => {
      socket?.off('connect', onConnect);
      offNotify();
    };
  }, [user, refresh]);

  const markRead = useCallback(async (id) => {
    if (!id) return;
    await notificationService.markRead(id);
    setItems((prev) => prev.map((n) => (String(n._id) === String(id) ? { ...n, readAt: new Date().toISOString() } : n)));
    // Decrement optimistically so the badge responds immediately, then reconcile
    // against the server — a row read in another tab must not be double-counted.
    setServerUnread((prev) => (prev == null ? prev : Math.max(0, prev - 1)));
    refreshUnread();
  }, [refreshUnread]);

  const value = useMemo(() => ({
    items,
    unreadCount,
    loading,
    open,
    setOpen,
    nextCursor,
    fetchMine,
    refresh,
    markRead,
  }), [items, unreadCount, loading, open, nextCursor, fetchMine, refresh, markRead]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
